import { ExternalLink } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatCurrencyVnd, formatDateVn } from "@/lib/utils";

interface Column {
  key: string;
  label: string;
  type?: "text" | "number" | "currency" | "date" | "datetime" | "badge" | "link";
  align?: "left" | "center" | "right";
  url_field?: string;
}

interface DataTablePayload {
  title: string;
  description?: string;
  columns: Column[];
  rows: Array<Record<string, unknown>>;
  actions_per_row?: Array<{ id: string; label: string; icon?: string }>;
}

export function DataTableWidget({
  payload,
}: {
  payload: DataTablePayload;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{payload.title}</CardTitle>
        {payload.description ? (
          <p className="text-sm text-muted-foreground">{payload.description}</p>
        ) : null}
      </CardHeader>
      <CardContent className="px-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/30">
            <tr>
              {payload.columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2 text-${c.align ?? "left"} font-medium text-muted-foreground text-xs`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payload.rows.map((row, idx) => (
              <tr key={idx} className="border-b last:border-b-0 hover:bg-muted/20">
                {payload.columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-3 py-2 text-${c.align ?? "left"}`}
                  >
                    {renderCell(row[c.key], row, c)}
                  </td>
                ))}
              </tr>
            ))}
            {payload.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={payload.columns.length}
                  className="px-3 py-8 text-center text-sm text-muted-foreground"
                >
                  Không có dữ liệu.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function renderCell(
  raw: unknown,
  row: Record<string, unknown>,
  col: Column,
): React.ReactNode {
  if (raw === null || raw === undefined || raw === "") return <span className="text-muted-foreground">—</span>;

  switch (col.type) {
    case "currency":
      return formatCurrencyVnd(Number(raw));
    case "date":
      return formatDateVn(String(raw));
    case "datetime":
      return new Date(String(raw)).toLocaleString("vi-VN");
    case "badge":
      return <Badge variant="secondary">{String(raw)}</Badge>;
    case "link": {
      const url = col.url_field ? String(row[col.url_field] ?? "#") : "#";
      return (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline inline-flex items-center gap-1"
        >
          {String(raw)}
          <ExternalLink className="size-3" />
        </a>
      );
    }
    case "number":
      return Number(raw).toLocaleString("vi-VN");
    default:
      // Many2one của Odoo trả về [id, name]
      if (Array.isArray(raw) && raw.length === 2) {
        return String(raw[1]);
      }
      return String(raw);
  }
}
